import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  Users,
  Calendar,
  FileText,
  ClipboardList,
  DollarSign,
  Shield,
  Brain,
  BarChart3,
  CheckCircle,
} from 'lucide-react'

const featureCategories = [
  {
    title: 'Client Management',
    icon: Users,
    features: [
      'Encrypted PHI storage at rest',
      'Guardian and contact management',
      'Insurance coverage tracking with verification',
      'Consent and document management',
      'Retention policy enforcement',
    ],
  },
  {
    title: 'Authorization Governance',
    icon: Shield,
    features: [
      'Real-time unit utilization ledger',
      'Hard stop when units exhausted',
      'Automatic expiration alerts',
      'Re-authorization workflow tracking',
      'Payer-specific rule configuration',
    ],
  },
  {
    title: 'Scheduling & Sessions',
    icon: Calendar,
    features: [
      'Staff-client availability matching',
      'Overlap prevention (client and staff)',
      'EVV-ready geolocation capture',
      'Cancellation and no-show tracking',
      'Automated appointment reminders',
    ],
  },
  {
    title: 'Clinical Documentation',
    icon: FileText,
    features: [
      'Note locking after signature',
      'Addendum-only edits post-lock',
      'Treatment plan versioning',
      'Goal and target tracking',
      'BCBA attestation workflow',
    ],
  },
  {
    title: 'Billing & Revenue Cycle',
    icon: DollarSign,
    features: [
      'Claims from locked notes only',
      'Pre-submission scrubber',
      'EDI 837P/835 support',
      'Denial work queues with CARC/RARC',
      'Timely filing deadline tracking',
    ],
  },
  {
    title: 'AI Assistance',
    icon: Brain,
    features: [
      'Session note summarization',
      'Grammar and clarity checks',
      'Addendum drafting assistance',
      'Full AI audit trail',
      'Human attestation required',
    ],
  },
]

const complianceFeatures = [
  'Immutable audit logs for all PHI access',
  'Role-based access with minimum necessary principle',
  'Session timeouts and device limits',
  'Configurable retention policies',
  'Legal hold support',
  'One-click audit packet export',
  'Incident response logging',
  'Staff credential tracking with expiration alerts',
]

export default function FeaturesPage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Features Built for Compliance
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              Every feature designed with audit defense in mind.
              Not compliance added later—enforcement logic that prevents forbidden states.
            </p>
          </div>
        </div>
      </div>

      {/* Feature Categories */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featureCategories.map((category) => (
              <div key={category.title} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <category.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">{category.title}</h2>
                </div>
                <ul className="space-y-2">
                  {category.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Section */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Compliance Infrastructure
            </h2>
            <p className="mt-4 text-lg text-gray-300">
              The foundation that makes audit defense possible
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {complianceFeatures.map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-3 bg-gray-800 rounded-lg p-4"
              >
                <CheckCircle className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <span className="text-sm text-gray-200">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            See It In Action
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Schedule a demo to see how these features work together to protect your practice.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary text-base px-6 py-3">
              Request Demo
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
